import { observer } from 'mobx-react-lite';
import { Button } from '@shared/components/common/Button';
import { ArrowLeft } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from '@shared/configs/constants';
import { useEffect, useState } from 'react';
import Heading from '@shared/components/common/Heading';
import { toastify } from '@shared/utils/toastify';
import { formatPrice } from '@shared/utils/utils';
import { PreCheckoutCartResponse } from '@shared/types/cart';
import { useStore } from '@shared/hooks/useStore';
import { flowResult } from 'mobx';
import { PUBLICATION_TYPE } from '@shared/types/enums';

export default observer(function CheckoutPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { cartStore } = useStore();
    const [isProcessing, setIsProcessing] = useState(false);

    const cartItems: PreCheckoutCartResponse | null = location.state?.cartItems || null;

    // Redirect to cart if no cartItems or invalid data
    useEffect(() => {
        if (!cartItems || !cartItems.publications || !cartItems.paymentInformation) {
            navigate(ROUTES.cart.href, { replace: true });
        }
    }, [cartItems, navigate]);

    // Early return if no cartItems (prevent rendering errors during redirect)
    if (!cartItems || !cartItems.publications || !cartItems.paymentInformation) {
        return null;
    }

    const handleBack = () => {
        navigate(-1);
    };

    const handlePayment = async () => {
        if (!cartItems || cartItems.publications.length === 0) {
            toastify('error', '商品を選択してください');
            return;
        }

        setIsProcessing(true);
        try {
            // Extract publication IDs
            const publicationIds = cartItems.publications.map(pub => pub.id.toString());

            // Get payment URL from API
            const response = await flowResult(cartStore.getPaymentUrl(
                publicationIds,
                cartItems.paymentInformation.priceTotal,
                cartItems.paymentInformation.taxTotal,
                cartItems.paymentInformation.grandTotal
            ));

            if (response) {
                localStorage.setItem('order_code', response.orderCode);
                if (response.isOrderFree) {
                    navigate(ROUTES.paymentResult.href.replace(':id', response.orderId.toString()));
                    return;
                }

                // Redirect to GMO payment gateway
                // GMO will redirect back to ROUTES.paymentResult.href after payment
                window.location.href = response.paymentUrl;
            }
        } catch (error: any) {
            console.error('Payment error:', error);
            toastify('error', error?.message || error?.response?.data?.message || '支払い処理に失敗しました');
            setIsProcessing(false);
        }
    };

    return (
        <div className='container mx-auto px-4 py-6 md:py-8 space-y-6'>
            {/* Page Title */}
            <div className='flex items-center gap-4 justify-between'>
                <Heading>カート</Heading>
                {/* Back Button */}
                <Button
                    onClick={handleBack}
                    className='min-w-40 gap-2'
                >
                    <ArrowLeft size={18} />
                    戻る
                </Button>
            </div>

            <div className='flex flex-col lg:flex-row gap-6 lg:gap-8'>
                {/* Left Section - Product List (Read-only) */}
                <div className='flex-1'>
                    <div className='bg-white rounded-lg border border-gray-200 overflow-hidden'>
                        {/* Table Header */}
                        <div className='hidden md:grid grid-cols-12 gap-4 p-4 bg-gray-50 border-b border-gray-200'>
                            <div className='col-span-8 font-semibold'>商品名</div>
                            <div className='col-span-4 font-semibold text-right'>価格（税抜）</div>
                        </div>

                        {/* Product Items */}
                        <div className='divide-y divide-gray-200'>
                            {cartItems.publications.map(item => (
                                <div
                                    key={item.id}
                                    className='grid grid-cols-12 gap-4 p-4'
                                >
                                    {/* Product Image and Info */}
                                    <div className='col-span-12 md:col-span-8 flex items-center gap-4'>
                                        <div className='flex-shrink-0'>
                                            <div className='w-20 h-28 md:w-24 md:h-32 bg-gray-100 rounded border border-gray-200 flex items-center justify-center overflow-hidden'>
                                                <img
                                                    src={item.linkImg}
                                                    alt={item.title}
                                                    className='w-full h-full object-cover'
                                                />
                                            </div>
                                        </div>
                                        <div className='flex-1 min-w-0 space-y-1.5'>
                                            <h3 className='font-medium mb-2 line-clamp-2'>{item.title}</h3>
                                            {item.type === PUBLICATION_TYPE.Book && (
                                                <p className='text-xs line-clamp-1'>{`著者：${item.author}`}</p>
                                            )}
                                            <p className='text-sm md:hidden'>{`${formatPrice(item.price)}（税抜）`}</p>
                                        </div>
                                    </div>

                                    {/* Price */}
                                    <div className='col-span-0 md:col-span-4 hidden md:flex items-center justify-end'>
                                        <div className='text-right'>
                                            <div className='font-bold'>
                                                {formatPrice(item.price)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}

                            <div className='py-8 px-4 flex justify-between font-medium'>
                                小計：
                                <span className='font-bold text-xl'>

                                    {formatPrice(cartItems.paymentInformation.priceTotal)}
                                </span>
                            </div>
                        </div>

                        {cartItems.publications.length === 0 && (
                            <div className='p-8 text-center text-gray-600'>
                                商品がありません
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Section - Summary */}
                <div className='lg:w-80 flex-shrink-0'>
                    <div className='sticky top-40 space-y-4'>
                        {/* Payment Summary */}
                        <div className='bg-[#F1F1F1] rounded-lg p-6 space-y-4'>
                            <div className='space-y-8'>
                                <div className='flex justify-between gap-2'>
                                    <span className='shrink-0'>税別支払金額</span>
                                    <span className='font-bold'>

                                        {formatPrice(cartItems.paymentInformation.priceTotal)}
                                    </span>
                                </div>
                                <div className='flex justify-between gap-2'>
                                    <span className='shrink-0'>消費税（10%）</span>
                                    <span className='font-bold'>

                                        {formatPrice(cartItems.paymentInformation.taxTotal)}
                                    </span>
                                </div>
                                <div className='border-t border-[#E0E0E0] pt-3 mt-3'>
                                    <div className='flex justify-between gap-2 text-lg font-bold'>
                                        <span className='shrink-0'>お支払い金額</span>
                                        <span>

                                            {formatPrice(cartItems.paymentInformation.grandTotal)}
                                        </span>
                                    </div>
                                </div>

                                <Button
                                    onClick={handlePayment}
                                    disabled={cartItems.publications.length === 0 || isProcessing}
                                    className='min-w-12 md:min-h-14 w-full text-base rounded-lg'
                                    size='lg'
                                >
                                    {isProcessing ? '処理中...' : '支払う'}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});
